import { z } from 'zod';
import { cursorPageSchema, isoDateTimeSchema, uuidSchema, localeSchema } from './common.js';
import { aiCapabilitySchema, aiFeedbackSchema, aiGenerationStatusSchema, aiProviderSchema, allergenSchema, dietaryLabelSchema, } from './enums.js';
import { recipeDraftSchema } from './recipe-draft.js';
import { recipeDiffSchema, recipeWriteInputSchema } from './recipes.js';
export const AI_PROMPT_VERSION = '1.0.0';
export const aiConstraintsSchema = z.object({
    servings: z.number().int().min(1).max(100).nullable().optional(),
    maxTotalMinutes: z.number().int().min(1).max(10080).nullable().optional(),
    dietaryPreferences: z.array(dietaryLabelSchema).max(20).optional(),
    avoidedIngredients: z.array(z.string().max(80)).max(50).optional(),
    cuisine: z.string().max(80).nullable().optional(),
    equipment: z.array(z.string().max(80)).max(20).optional(),
    language: localeSchema.optional(),
    variants: z.number().int().min(1).max(3).default(1),
});
export const recipeFromTextRequestSchema = z.object({
    text: z.string().trim().min(3).max(20000),
    mode: z.enum(['keywords', 'notes', 'auto']).default('auto'),
    constraints: aiConstraintsSchema.optional(),
    /** Answers to previously returned followUpQuestions. */
    answers: z.record(z.string().max(500)).optional(),
    previousGenerationId: uuidSchema.optional(),
});
export const recipeFromImagesRequestSchema = z.object({
    assetIds: z.array(uuidSchema).min(1).max(6),
    description: z.string().trim().max(2000).optional(),
    constraints: aiConstraintsSchema.optional(),
    answers: z.record(z.string().max(500)).optional(),
    previousGenerationId: uuidSchema.optional(),
});
/**
 * A public YouTube video (watch, youtu.be, shorts, live or embed link) the model watches and
 * writes down. No constraints: the recipe is transcribed as cooked, not adapted.
 */
export const recipeFromVideoRequestSchema = z.object({
    url: z.string().trim().url().max(2000),
    /** A hint for the model, e.g. which of several dishes in the video is wanted. */
    description: z.string().trim().max(2000).optional(),
    answers: z.record(z.string().max(500)).optional(),
    previousGenerationId: uuidSchema.optional(),
});
export const imageInputKindSchema = z.enum([
    'DISH_PHOTO',
    'RECIPE_DOCUMENT',
    'INGREDIENTS',
    'UNKNOWN',
]);
export const aiUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative().nullable(),
    latencyMs: z.number().int().nonnegative(),
});
/** Output for image generations includes the classification and OCR text for review. */
export const imageGenerationResultSchema = z.object({
    inputKind: imageInputKindSchema,
    ocrText: z.string().nullable(),
    drafts: z.array(recipeDraftSchema),
});
/** Output of a video generation: the watched video (canonical link) and the draft. */
export const videoGenerationResultSchema = z.object({
    videoUrl: z.string().url(),
    drafts: z.array(recipeDraftSchema),
});
export const textGenerationResultSchema = z.object({
    drafts: z.array(recipeDraftSchema),
});
export const aiGenerationSchema = z.object({
    id: uuidSchema,
    capability: aiCapabilitySchema,
    provider: aiProviderSchema.nullable(),
    model: z.string().nullable(),
    promptVersion: z.string(),
    schemaVersion: z.string(),
    status: aiGenerationStatusSchema,
    progress: z.number().int().min(0).max(100),
    result: z.unknown().nullable(),
    error: z.string().nullable(),
    /** Machine-readable cause of a failure (WORKER_ERROR_CODES); clients translate it. */
    errorCode: z.string().nullable().optional(),
    usage: aiUsageSchema.nullable(),
    feedback: aiFeedbackSchema.nullable(),
    createdAt: isoDateTimeSchema,
    completedAt: isoDateTimeSchema.nullable(),
});
export const aiGenerationPageSchema = cursorPageSchema(aiGenerationSchema);
export const aiFeedbackRequestSchema = z.object({
    feedback: aiFeedbackSchema,
    reason: z.string().max(1000).optional(),
});
/** Server-sent event payloads for /ai/generations/{id}/events and conversation streams. */
export const aiEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('status'),
        status: aiGenerationStatusSchema,
        progress: z.number().int(),
    }),
    z.object({ type: z.literal('delta'), text: z.string() }),
    z.object({ type: z.literal('question'), questions: z.array(z.string()) }),
    z.object({ type: z.literal('result'), generationId: uuidSchema, result: z.unknown() }),
    z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
    z.object({ type: z.literal('done') }),
]);
// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------
export const createConversationRequestSchema = z.object({
    recipeId: uuidSchema.nullable().optional(),
    title: z.string().max(120).optional(),
    initialMessage: z.string().trim().min(1).max(8000).optional(),
});
export const conversationMessageRoleSchema = z.enum(['user', 'assistant', 'system']);
export const conversationProposalSchema = z.object({
    kind: z.enum(['NEW_RECIPE', 'MODIFY_RECIPE', 'VARIATION', 'ANSWER']),
    draft: recipeDraftSchema.nullable(),
    /** For MODIFY_RECIPE: the full proposed write input plus the structured diff. */
    proposedRecipe: recipeWriteInputSchema.nullable(),
    diff: recipeDiffSchema.nullable(),
    summary: z.string(),
    applied: z.boolean().default(false),
});
export const conversationMessageSchema = z.object({
    id: uuidSchema,
    role: conversationMessageRoleSchema,
    content: z.string(),
    proposal: conversationProposalSchema.nullable(),
    generationId: uuidSchema.nullable(),
    /** Language an assistant reply is written in (it mirrors the user); sets `lang` and the read-aloud voice. */
    language: z.string().nullable().optional(),
    createdAt: isoDateTimeSchema,
});
export const conversationSchema = z.object({
    id: uuidSchema,
    recipeId: uuidSchema.nullable(),
    title: z.string(),
    messages: z.array(conversationMessageSchema),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const conversationSummarySchema = conversationSchema.omit({ messages: true }).extend({
    lastMessagePreview: z.string().nullable(),
});
export const sendMessageRequestSchema = z.object({
    content: z.string().trim().min(1).max(8000),
    /** Optional structured intent to guide the assistant. */
    intent: z
        .enum([
        'CHAT',
        'SUBSTITUTE',
        'ADAPT_SERVINGS',
        'ADAPT_DIET',
        'ADAPT_TIME',
        'ADAPT_EQUIPMENT',
        'EXPLAIN',
        'LEFTOVERS',
        'VARIATION',
    ])
        .default('CHAT'),
});
export const sendMessageResponseSchema = z.object({
    userMessage: conversationMessageSchema,
    generationId: uuidSchema,
    /** SSE stream URL for the assistant reply. */
    streamUrl: z.string(),
});
export const applyProposalRequestSchema = z.object({
    messageId: uuidSchema,
    mode: z.enum(['APPLY_TO_RECIPE', 'CREATE_VARIATION', 'CREATE_NEW']),
    expectedVersion: z.number().int().optional(),
    variationLabel: z.string().max(80).optional(),
});
// ---------------------------------------------------------------------------
// Source search
// ---------------------------------------------------------------------------
export const sourceSearchRequestSchema = z.object({
    query: z.string().trim().min(3).max(500),
    language: localeSchema.optional(),
    maxResults: z.number().int().min(1).max(10).default(5),
});
export const sourceCandidateSchema = z.object({
    title: z.string(),
    url: z.string().url(),
    domain: z.string(),
    snippet: z.string().nullable(),
    /** Why this source looks relevant. Retrieved facts are separated from AI inference. */
    explanation: z.string(),
    retrievedFacts: z.array(z.string()).default([]),
    inference: z.string().nullable().default(null),
    hasStructuredRecipe: z.boolean().nullable().default(null),
    importable: z.boolean(),
});
export const sourceSearchResultSchema = z.object({
    generationId: uuidSchema,
    query: z.string(),
    candidates: z.array(sourceCandidateSchema),
    /** True when no reliable source was found. The UI then offers an unsourced AI recipe. */
    noReliableSource: z.boolean(),
    unsourcedOfferAvailable: z.boolean(),
    providerNote: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Cooking mode questions & suggestions
// ---------------------------------------------------------------------------
export const cookingQuestionRequestSchema = z.object({
    recipeId: uuidSchema,
    currentStepId: uuidSchema.nullable().optional(),
    currentServings: z.string().optional(),
    question: z.string().trim().min(1).max(1000),
});
export const cookingQuestionResponseSchema = z.object({
    answer: z.string(),
    /** True when answered purely from the recipe without external inference. */
    fromRecipe: z.boolean(),
    notInRecipe: z.boolean(),
    generationId: uuidSchema.nullable(),
});
export const mealSuggestionRequestSchema = z.object({
    dateFrom: z.string(),
    dateTo: z.string(),
    slots: z.array(z.string()).default(['DINNER']),
    usePantry: z.boolean().default(true),
    maxMinutesPerMeal: z.number().int().positive().optional(),
});
export const mealSuggestionSchema = z.object({
    date: z.string(),
    slot: z.string(),
    recipeId: uuidSchema.nullable(),
    title: z.string(),
    reason: z.string(),
});
export const mealSuggestionResponseSchema = z.object({
    generationId: uuidSchema,
    suggestions: z.array(mealSuggestionSchema),
});
export const aiConsentStatusSchema = z.object({
    /** AI features are unlocked for this account by an administrator. */
    enabled: z.boolean(),
    consented: z.boolean(),
    consentedAt: isoDateTimeSchema.nullable(),
    providers: z.array(aiProviderSchema),
    region: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Internal generation inputs/outputs shared by the API (producer) and the worker
// (consumer). Stored sanitized on AIGeneration.input / AIGeneration.output.
// ---------------------------------------------------------------------------
export const aiTextGenerationInputSchema = recipeFromTextRequestSchema.extend({
    language: localeSchema.optional(),
});
export const aiImageGenerationInputSchema = recipeFromImagesRequestSchema.extend({
    language: localeSchema.optional(),
});
/** `url` is the canonical watch URL (the API normalizes the pasted link). */
export const aiVideoGenerationInputSchema = recipeFromVideoRequestSchema.extend({
    language: localeSchema.optional(),
});
export const aiConversationGenerationInputSchema = z.object({
    conversationId: uuidSchema,
    messageId: uuidSchema,
    intent: sendMessageRequestSchema.shape.intent,
    language: localeSchema.optional(),
});
export const aiSourceSearchInputSchema = sourceSearchRequestSchema;
export const aiCookingQuestionInputSchema = cookingQuestionRequestSchema.extend({
    language: localeSchema.optional(),
});
export const aiMealSuggestionInputSchema = mealSuggestionRequestSchema.extend({
    language: localeSchema.optional(),
});
export const tagSuggestionRequestSchema = z.object({
    recipeId: uuidSchema.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(5000).nullable().optional(),
    ingredientNames: z.array(z.string().max(200)).max(200).optional(),
    stepInstructions: z.array(z.string().max(5000)).max(200).optional(),
    language: localeSchema.optional(),
});
export const tagSuggestionResultSchema = z.object({
    generationId: uuidSchema.nullable(),
    tags: z.array(z.string()),
    categories: z.array(z.string()),
    dietaryLabels: z.array(dietaryLabelSchema),
    allergens: z.array(allergenSchema),
});
export const aiImportStructuringInputSchema = z.object({
    pageText: z.string().max(200000),
    url: z.string().url().nullable().optional(),
    language: localeSchema.optional(),
});
export const aiTranscriptionInputSchema = z.object({
    assetId: uuidSchema,
    language: localeSchema.nullable().optional(),
    context: z.enum(['command', 'dictation']).default('dictation'),
});
/** Output stored for TRANSCRIPTION generations (never the audio itself). */
export const transcriptionResultSchema = z.object({
    text: z.string(),
    language: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    provider: z.string(),
});
/** Output stored for CONVERSATION generations: the persisted assistant message. */
export const conversationTurnResultSchema = z.object({
    message: conversationMessageSchema,
});
export const cookingQuestionResultSchema = z.object({
    answer: z.string(),
    fromRecipe: z.boolean(),
    notInRecipe: z.boolean(),
});
export const mealSuggestionResultSchema = z.object({
    suggestions: z.array(mealSuggestionSchema),
});
//# sourceMappingURL=ai.js.map